import React, { useEffect, useMemo, useState } from "react";
import { Droplets, Flame, Scale, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const REFRESH_MS = 5 * 60 * 1000; // ingest is hourly; 5-min poll is plenty

const num = (v, f = 0) => (Number.isFinite(Number(v)) ? Number(v) : f);
const oiText = (v) => {
  const n = num(v);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${Math.round(n).toLocaleString()}`;
};

function Spark({ points, color, height = 26 }) {
  const path = useMemo(() => {
    const vals = points.filter((v) => Number.isFinite(v));
    if (vals.length < 2) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const span = max - min || 1;
    return vals
      .map((v, i) => `${((i / (vals.length - 1)) * 100).toFixed(2)},${(height - 3 - ((v - min) / span) * (height - 6)).toFixed(2)}`)
      .join(" ");
  }, [points, height]);
  if (!path) return <div style={{ height, opacity: 0.35, fontSize: 10, display: "flex", alignItems: "center" }}>building history…</div>;
  return (
    <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width: "100%", height, display: "block" }}>
      <polyline points={path} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Stat({ label, value, tone, series, color }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, letterSpacing: ".08em", textTransform: "uppercase", opacity: 0.55 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: tone || "inherit", margin: "1px 0 3px" }}>{value}</div>
      <Spark points={series} color={color} />
    </div>
  );
}

function CoinCard({ ccy, rows }) {
  const last = rows[rows.length - 1] || {};
  const liqLong = num(last.liq_long_sz);
  const liqShort = num(last.liq_short_sz);
  const liqTotal = liqLong + liqShort || 1;
  const longPct = (liqLong / liqTotal) * 100;
  const fundingBp = num(last.funding_rate) * 10000;
  const buyShare = num(last.taker_buy_share_1h, 0.5) * 100;
  const series = (key) => rows.map((r) => num(r[key], NaN));
  return (
    <div className="perp-flow-card" style={{ background: "rgba(10,12,20,.55)", border: "1px solid rgba(148,163,184,.14)", borderRadius: 12, padding: "12px 14px", flex: "1 1 230px", minWidth: 220 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 14 }}>{ccy}</strong>
        <span style={{ fontSize: 11, opacity: 0.6 }}>perp · OI {oiText(last.oi_usd)}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <Stat label="Funding" value={`${fundingBp >= 0 ? "+" : ""}${fundingBp.toFixed(2)}bp`}
          tone={fundingBp > 1.5 ? "#fbbf24" : undefined} series={series("funding_rate")} color="#fbbf24" />
        <Stat label="Long/Short" value={num(last.long_short_ratio).toFixed(2)}
          series={series("long_short_ratio")} color="#60a5fa" />
        <Stat label="Taker buy" value={`${buyShare.toFixed(0)}%`}
          tone={buyShare >= 55 ? "#34d399" : buyShare <= 45 ? "#f87171" : undefined}
          series={series("taker_buy_share_1h")} color="#34d399" />
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, letterSpacing: ".06em", opacity: 0.6, marginBottom: 3 }}>
          <span>LONGS LIQ&apos;D {num(last.liq_long_count)}</span><span>{num(last.liq_short_count)} SHORTS LIQ&apos;D</span>
        </div>
        <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", background: "rgba(148,163,184,.12)" }}>
          <div style={{ width: `${longPct}%`, background: "#f87171" }} />
          <div style={{ width: `${100 - longPct}%`, background: "#34d399" }} />
        </div>
      </div>
    </div>
  );
}

export default function PerpFlowPanel() {
  const [series, setSeries] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${API}/researcher/lab/okx/flow?hours=48`);
        if (!r.ok) throw new Error(String(r.status));
        const body = await r.json();
        if (alive) { setSeries(body.series || {}); setError(false); }
      } catch {
        if (alive) setError(true);
      }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const coins = Object.keys(series || {}).sort();
  if (error || (series && !coins.length)) return null; // feed not populated yet — stay out of the way

  return (
    <section className="market-room-panel" style={{ marginTop: 14 }}>
      <div className="market-room-panel-head">
        <div><span>OKX perp order flow</span><strong>Funding · positioning · liquidations</strong></div>
        <div style={{ display: "flex", gap: 10, opacity: 0.55, fontSize: 11, alignItems: "center" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Flame style={{ width: 12, height: 12 }} />hourly</span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Droplets style={{ width: 12, height: 12 }} />liq skew</span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Scale style={{ width: 12, height: 12 }} />L/S</span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><TrendingUp style={{ width: 12, height: 12 }} />taker</span>
        </div>
      </div>
      {!series ? (
        <div style={{ opacity: 0.5, fontSize: 12, padding: "10px 2px" }}>Loading perp flow…</div>
      ) : (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
          {coins.map((c) => <CoinCard key={c} ccy={c} rows={series[c]} />)}
        </div>
      )}
    </section>
  );
}
